import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import AppsPage from "../Pages/ABAC/AppsPage/AppsPage";
import AbacClientPage from "../Pages/ABAC/ClientPage/AbacClientPage";
import { ManageAttribute } from "../Pages/ABAC/ManageAppPage/Components/Attributes/Components/ManageAttribute/ManageAttribute";
import { ManageFeature } from "../Pages/ABAC/ManageAppPage/Components/Features/Components/ManageFeature/ManageFeature";
import ManageAppPage from "../Pages/ABAC/ManageAppPage/ManageAppPage";
import ManageUserPage from "../Pages/ABAC/ManageUserPage/ManageUserPage";
import AbacUsersPage from "../Pages/ABAC/UsersPage/AbacUsersPage";
import AdminUserManagementPage from "../Pages/Admin/AdminUserManagementPage/AdminUserManagementPage";
import AdminUsersPage from "../Pages/Admin/AdminUsersPage/AdminUsersPage";
import AncillaryPage from "../Pages/AncillaryPage/AncillaryPage";
import BannerPage from "../Pages/BannerPage/BannerPage";
import CustomRadiusPage from "../Pages/CustomRadiusPage/CustomRadiusPage";
import DisplayPage from "../Pages/DisplayPage/DisplayPage";
import EmailApplyLinkPage from "../Pages/EmailApplyLinkPage/EmailApplyLinkPage";
import EventSearchPage from "../Pages/EventSearchPage/EventSearchPage";
import EventTabs from "../Pages/EventsPage/Components/Tabs/EventTabs";
import EventsPage from "../Pages/EventsPage/EventsPage";
import EventSponsorDetailed from "../Pages/EventSponsorsPage/Components/EventSponsorDetailed";
import EventSponsorsPage from "../Pages/EventSponsorsPage/EventSponsorsPage";
import EventTypesPage from "../Pages/EventTypesPage/EventTypesPage";
import EventVenueDetailed from "../Pages/EventVenuesPage/Components/EventVenueDetailed";
import EventVenuesPage from "../Pages/EventVenuesPage/EventVenuesPage";
import ExternalAccountsTabs from "../Pages/ExternalAccountsPage/ExternalAccountsPage";
import FeaturedEventsTabs from "../Pages/FeaturedEventsPage/FeaturedEventsTabs";
import FeaturedGroupDetailed from "../Pages/FeaturedGroupsPage/Components/FeaturedGroupDetailed/FeaturedGroupDetailed";
import FeaturedGroupsPage from "../Pages/FeaturedGroupsPage/FeaturedGroupsPage";
import FeaturedJobs from "../Pages/FeaturedJobsPage/FeaturedJobsPage";
import FormsPage from "../Pages/FormsPage/FormsPage";
import JobGroupDetailed from "../Pages/JobGroupsPage/Components/JobGroupDetailed";
import JobGroupsTabs from "../Pages/JobGroupsPage/Components/JobGroupsTabs";
import ManageLocationDetailed from "../Pages/LocationsPage/Components/Tabs/ManageLocations/ManageLocationDetailed";
import LocationsPage from "../Pages/LocationsPage/LocationsPage";
import MainPage from "../Pages/MainPage/MainPage";
import MapConfigurationPage from "../Pages/MapConfigurationPage/MapConfigurationPage";
import MasterCachePage from "../Pages/MasterCachePage/MasterCachePage";
import PrefilteredLinksPage from "../Pages/PrefilteredLinksPage/PrefilteredLinksPage";
import ProfilePage from "../Pages/ProfilePage/ProfilePage";
import ReportUser from "../Pages/ReportsPage/Components/ReportUser";
import ReportsPage from "../Pages/ReportsPage/ReportsPage";
import ResultsPage from "../Pages/ResultsPage/ResultsPage";
import TrackingPixelDetailed from "../Pages/TrackingPixelsPage/Components/TrackingPixelDetailed";
import TrackingPixelsPage from "../Pages/TrackingPixelsPage/TrackingPixelsPage";
import UserDetailed from "../Pages/UsersPage/Components/UserDetailedPage";
import UsersPage from "../Pages/UsersPage/UsersPage";
import theme, { adminTheme } from "../theme";
import "./App.css";
import NavBar from "./Components/Navigation/NavBar";
import ProtectedRoute from "./Components/Navigation/ProtectedRoute";
import { RenderSnackbar } from "./Components/Snackbar/Snackbar";
import { Rights } from "./Enums/Rights";
import { Roles } from "./Enums/Roles";
import { RouteTypes } from "./Enums/RouteTypes";
import { basicSort } from "./Helpers/BasicSort";
import { selectBaseClient } from "./Helpers/clientSelection";
import { getUserBaseClientAccess } from "./Services/AdminService";
import { getAllBaseClients } from "./Services/ClientService";
import { useFeatureToggles } from "./State/Hooks/useFeatureToggles";
import { useNotification } from "./State/Hooks/useNotificationHooks";
import { usePendoInitialize } from "./State/Hooks/usePendoScript";
import { updateClients } from "./State/Slices/AllClientsSlice";
import { selectCareersClientValue } from "./State/Slices/SelectedCareersClientSlice";
import { selectClientValue } from "./State/Slices/SelectedClientSlice";
import { updateInformation } from "./State/Slices/UserInformationSlice";
import { updatePermissions } from "./State/Slices/UserPermissionsSlice";

function App() {
  const { isLoading, loginWithRedirect, isAuthenticated, error, user, getAccessTokenSilently } = useAuth0();

  useFeatureToggles(user);
  usePendoInitialize();

  const dispatch = useDispatch();
  const { displayWarning } = useNotification();
  const selectedClient = useSelector(selectClientValue);
  const selectedCareersClient = useSelector(selectCareersClientValue);

  // Bootstrap lifecycle: 'idle' | 'loading' | 'success' | 'no-access' | 'error'
  const [bootstrapStatus, setBootstrapStatus] = useState("idle");
  // Bumping this re-runs the bootstrap effect (used by the "Retry" action).
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  // Guards against overlapping bootstrap runs.
  const bootstrapInFlight = useRef(false);

  const hasSelectedClient = Boolean(selectedClient?.id);

  axios.defaults.headers.common["client"] = selectedClient.id;

  /**
   * Loads the user's base-client access list, filters/sorts the clients they
   * can see and selects one. Returns the resulting bootstrap status. Throws on
   * API failure so the calling effect can retry.
   */
  async function loadClients() {
    // Buffer is required for handling the Base64 decoding
    const { Buffer: buffer } = await import("buffer");

    // Fetch the user's base client access roles using their activateId
    const response = await getUserBaseClientAccess(user["http://nasrecruitment.io/activateId"]);

    // Decode the Base64 response and parse it into an object
    const rolesObject = JSON.parse(buffer.from(response, "base64").toString("ascii"));

    if (!Array.isArray(rolesObject)) {
      throw new Error("Invalid rolesObject format, expected an array.");
    }

    dispatch(updatePermissions(rolesObject));

    // Fetch all base clients and keep only those the user has access to
    const allBaseClients = await getAllBaseClients();
    const baseClientIds = new Set(rolesObject.map((role) => role.BaseClientID));
    const filteredClients = allBaseClients
      .filter((client) => baseClientIds.has(client.id))
      .sort((a, b) => basicSort(a.clientName, b.clientName));

    dispatch(updateClients(filteredClients));

    // No clients means the user has no access to anything in this portal.
    if (filteredClients.length === 0) {
      return "no-access";
    }

    // Select the first client when none is selected or the persisted selection
    // is no longer valid for this user.
    const currentIsValid =
      selectedClient.id != null &&
      filteredClients.some((c) => c.id === selectedClient.id);

    if (!currentIsValid) {
      // A failed careers lookup still leaves a usable base client selected, so
      // bootstrap succeeds either way; the helper surfaces a warning instead.
      await selectBaseClient(filteredClients[0], dispatch, {
        onCareersError: () =>
          displayWarning(
            "We couldn't load careers data for this client. Some dashboard widgets may be unavailable."
          ),
      });
    }

    return "success";
  }

  // Register a single axios request interceptor that attaches the Auth0 bearer
  // token and the user email header. The old code re-registered the auth
  // interceptor on every render (and a separate user-header interceptor per
  // user change) without ever ejecting them — leaking a new interceptor each
  // render, multiplying token fetches and the surface for a request to fail.
  // This registers once and ejects on cleanup.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const interceptorId = axios.interceptors.request.use(async function (config) {
      const accessToken = await getAccessTokenSilently();
      config.headers.Authorization = `Bearer ${accessToken}`;
      if (user?.email) {
        config.headers["user"] = user.email;
      }
      return config;
    });

    return () => axios.interceptors.request.eject(interceptorId);
  }, [isAuthenticated, getAccessTokenSilently, user]);

  // Persist the authenticated user's info once it is available.
  useEffect(() => {
    if (user) {
      dispatch(updateInformation(user));
    }
  }, [user, dispatch]);

  // Bootstrap the client list after authentication. This used to run during
  // render, which could kick off duplicate concurrent runs; it now runs off the
  // render path with a bounded retry for transient API failures. A single
  // failure here previously bricked the session because `allClients` had
  // already been populated, so the render-time guard never let it run again.
  useEffect(() => {
    if (!isAuthenticated || !user || bootstrapInFlight.current) {
      return undefined;
    }

    let cancelled = false;
    bootstrapInFlight.current = true;
    setBootstrapStatus("loading");

    (async () => {
      const maxAttempts = 3;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const status = await loadClients();
          if (!cancelled) setBootstrapStatus(status);
          break;
        } catch (err) {
          // A 404 from the access endpoint means the user has no client access.
          if (err?.response?.status === 404) {
            if (!cancelled) setBootstrapStatus("no-access");
            break;
          }
          if (attempt === maxAttempts) {
            console.error("Error bootstrapping clients:", err);
            if (!cancelled) setBootstrapStatus("error");
            break;
          }
          // Linear backoff before retrying a transient failure.
          await new Promise((resolve) => setTimeout(resolve, attempt * 750));
        }
      }
      // Only release the guard if this run wasn't superseded; a cancelled run
      // must not clear the flag out from under the run that replaced it.
      if (!cancelled) {
        bootstrapInFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
      // Release the in-flight guard so a superseding effect run (e.g. an Auth0
      // user/isAuthenticated reference change mid-bootstrap) can start a fresh
      // bootstrap instead of being blocked and leaving the app stuck loading.
      bootstrapInFlight.current = false;
    };
    // `loadClients` is intentionally omitted: it is recreated every render and
    // closes over values that would otherwise re-trigger this effect. Bootstrap
    // is deliberately gated on auth/user and the explicit retry counter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, bootstrapAttempt]);

  useEffect(() => {
    axios.defaults.headers.common["baseClientId"] = selectedClient.id;
    //TODO: decide where this ID should come from/how it should be stored
    axios.defaults.headers.common["ClientAppId"] = "60A7A73A-31C1-4548-B0F4-BF7FBDC3303F";

    if (selectedCareersClient?.id) {
      axios.defaults.headers.common["careersClientId"] = selectedCareersClient.id;
    }
  }, [selectedCareersClient, selectedClient.id]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://www.bing.com/api/maps/mapcontrol?callback=GetMap&key=${import.meta.env.VITE_BING_MAP_KEY}`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleRetryBootstrap = () => setBootstrapAttempt((attempt) => attempt + 1);

  if (isLoading) {
    return <div>loading...</div>;
  }

  if (error) {
    return <div>Oops... {error.message}</div>;
  }

  if (isAuthenticated) {
    // When there is no usable client selected yet, show an explicit state
    // instead of dropping the user into the app, where pages such as the
    // dashboard would otherwise render blank skeletons forever.
    if (!hasSelectedClient) {
      if (bootstrapStatus === "error") {
        return (
          <WorkspaceMessage
            title="We couldn't load your workspace"
            message="Something went wrong while loading your clients. Please try again."
            action={{ label: "Retry", onClick: handleRetryBootstrap }}
          />
        );
      }

      if (bootstrapStatus === "no-access") {
        return (
          <WorkspaceMessage
            title="No client access"
            message="Your account isn't linked to any clients. Please contact your administrator."
          />
        );
      }

      return <WorkspaceMessage loading title="Loading your workspace…" />;
    }

    return (
      <>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <Router>
              <NavBar />
              <Switch>
                <Route path="/" exact component={MainPage} />
                <ProtectedRoute
                  path="/featuredgroups"
                  exact
                  component={FeaturedGroupsPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.FeaturedJobs}
                />
                <ProtectedRoute
                  path="/featuredgroups/featuredgroupsdetailed"
                  exact
                  component={FeaturedGroupDetailed}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.FeaturedJobs}
                />
                <ProtectedRoute
                  path="/featuredevents"
                  exact
                  component={FeaturedEventsTabs}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Events}
                />
                <ProtectedRoute
                  path="/featuredjobs"
                  exact
                  component={FeaturedJobs}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.FeaturedJobs}
                />
                <ProtectedRoute
                  path="/eventsponsors"
                  exact
                  component={EventSponsorsPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.EventSponsors}
                />
                <ProtectedRoute
                  path="/eventsponsors/eventsponsordetailed"
                  exact
                  component={EventSponsorDetailed}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.EventSponsors}
                />
                <ProtectedRoute
                  path="/jobgroups"
                  exact
                  component={JobGroupsTabs}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/eventvenues"
                  exact
                  component={EventVenuesPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.EventVenues}
                />
                <ProtectedRoute
                  path="/eventvenues/eventvenuedetailed"
                  exact
                  component={EventVenueDetailed}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.EventVenues}
                />
                <ProtectedRoute
                  path="/jobgroups/jobgroupdetailed"
                  exact
                  component={JobGroupDetailed}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/locations/locationdetailed"
                  exact
                  component={ManageLocationDetailed}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Locations}
                />
                <ProtectedRoute
                  path="/emailapplylink"
                  exact
                  component={EmailApplyLinkPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/trackingpixels"
                  exact
                  component={TrackingPixelsPage}
                  routeType={RouteTypes.ClientRoleProtected}
                  requiredPermissions={Roles.SuperAdmin}
                />
                <ProtectedRoute
                  path="/trackingpixels/trackingpixeldetailed"
                  exact
                  component={TrackingPixelDetailed}
                  routeType={RouteTypes.ClientRoleProtected}
                  requiredPermissions={Roles.SuperAdmin}
                />
                <ProtectedRoute
                  path="/externalaccounts"
                  exact
                  component={ExternalAccountsTabs}
                  routeType={RouteTypes.ClientRoleProtected}
                  requiredPermissions={Roles.SuperAdmin}
                />
                <ProtectedRoute
                  path="/eventtypes"
                  exact
                  component={EventTypesPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.EventTypes}
                />
                <ProtectedRoute
                  path="/mastercache"
                  exact
                  component={MasterCachePage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/eventsearch"
                  exact
                  component={EventSearchPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/customradius"
                  exact
                  component={CustomRadiusPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/events"
                  exact
                  component={EventsPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Events}
                />
                <ProtectedRoute
                  path="/events/eventdetailed"
                  exact
                  component={EventTabs}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Events}
                />
                <ProtectedRoute
                  path="/prefilteredlinks"
                  exact
                  component={PrefilteredLinksPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.PrefilteredLinks}
                />
                <ProtectedRoute
                  path="/banner"
                  exact
                  component={BannerPage}
                  routeType={RouteTypes.ClientRoleProtected}
                  requiredPermissions={Roles.SuperAdmin}
                />
                <ProtectedRoute
                  path="/mapconfiguration"
                  exact
                  component={MapConfigurationPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/ancillary"
                  exact
                  component={AncillaryPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/jobalertsreport"
                  exact
                  component={ReportsPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.TalentNetworkData}
                  componentProps={{ optIn: false }}
                />
                <ProtectedRoute
                  path="/marketreport"
                  exact
                  component={ReportsPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.TalentNetworkData}
                  componentProps={{ optIn: true }}
                />
                <ProtectedRoute
                  path="/marketreport/reportuser"
                  exact
                  component={ReportUser}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.TalentNetworkData}
                />
                <Route path="/profile" exact component={ProfilePage} />
                <ProtectedRoute
                  path="/display"
                  exact
                  component={DisplayPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/forms"
                  exact
                  component={FormsPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/results"
                  exact
                  component={ResultsPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Configurations}
                />
                <ProtectedRoute
                  path="/locations"
                  exact
                  component={LocationsPage}
                  routeType={RouteTypes.ClientRightProtected}
                  requiredPermissions={Rights.Locations}
                />
                <ProtectedRoute
                  path="/users"
                  exact
                  component={UsersPage}
                  routeType={RouteTypes.ClientCrmRightsProtected}
                  requiredPermissions={Rights.Users}
                />
                <ProtectedRoute
                  path="/users/usersdetailed"
                  exact
                  component={UserDetailed}
                  routeType={RouteTypes.ClientCrmRightsProtected}
                  requiredPermissions={Rights.Users}
                />
                <ProtectedRoute
                  path="/abac"
                  exact
                  component={AppsPage}
                  routeType={RouteTypes.ClientRoleProtected}
                  requiredPermissions={Roles.SuperAdmin}
                />
                <Route path="/abac/:id" exact component={ManageAppPage} />
                <Route path="/abac/:appId/feature/:featureId" exact component={ManageFeature} />
                <Route path="/abac/:appId/attribute/:attributeId" exact component={ManageAttribute} />
                <Route path="/abacClient" exact component={AbacClientPage} />
                <Route path="/abacUsers" exact component={AbacUsersPage} />
                <Route path="/abacUsers/:id" exact component={ManageUserPage} />
                <ThemeProvider theme={adminTheme}>
                  <Route path="/adminUserManagement" exact component={AdminUsersPage} />
                  <Route path="/adminUserManagement/:id" exact component={AdminUserManagementPage} />
                </ThemeProvider>
              </Switch>
            </Router>
          </ThemeProvider>
        </StyledEngineProvider>
        <RenderSnackbar />
      </>
    );
  } else {
    loginWithRedirect();
  }
}

/**
 * Full-screen status shown while the workspace is bootstrapping, or when it
 * can't be loaded (no client access / load error). Replaces the previous
 * behaviour of dropping straight into the app with ever-loading skeletons.
 */
function WorkspaceMessage({ title, message, action, loading }) {
  return (
    <Box
      data-test="workspace-status"
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: 3,
        textAlign: "center",
      }}
    >
      {loading && <CircularProgress />}
      <Typography variant="h6">{title}</Typography>
      {message && (
        <Typography color="text.secondary" sx={{ maxWidth: 440 }}>
          {message}
        </Typography>
      )}
      {action && (
        <Button variant="contained" onClick={action.onClick} data-test="workspace-retry">
          {action.label}
        </Button>
      )}
    </Box>
  );
}

export default App;
